/*
 * Product summary feature grid (sections/product-summary.liquid).
 * - Tiles rise in once, staggered, as they scroll into view ([data-ps-enter] + .is-in).
 * - Every CSS loop (photo overlays, the floating box art, the caret) holds off screen
 *   and in a hidden tab ([data-asleep]).
 * - [data-ps-count="from|to"] counts up each time the section comes into view, keeping
 *   the target's format ("$99.99", "2,400", "35%").
 * - [data-ps-timer="mm:ss"] counts down once a second while on screen, then starts over.
 * - Keycaps show Ctrl / Alt instead of ⌘ / ⌥ for Windows visitors.
 * - [data-ps-presets] timeline tiles drop a different preset each loop.
 * Reduced motion: no entrance, numbers show their final value, the timer stands still.
 */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function still() { return !!(reduce && reduce.matches); }

  function isWindows() {
    var p = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent || '';
    return /win/i.test(p);
  }

  function initKeys(root) {
    if (!isWindows()) return;
    var map = { '⌘': 'Ctrl', '⌥': 'Alt', '⌃': 'Ctrl' };
    root.querySelectorAll('[data-ps-key]').forEach(function (k) {
      var t = k.textContent.trim();
      if (map[t]) {
        k.textContent = map[t];
        k.classList.add('psum-key--word');
      }
    });
  }

  function initEntrance(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('.psum-art, .psum-tile'));
    if (!items.length) return;
    if (still() || !('IntersectionObserver' in window)) return;
    // Already on screen when the script runs: show it as is, no entrance.
    if (root.getBoundingClientRect().top < window.innerHeight * 0.75) return;
    root.setAttribute('data-ps-enter', '');
    var io = new IntersectionObserver(function (entries) {
      var batch = entries.filter(function (e) { return e.isIntersecting; }).map(function (e) { return e.target; });
      batch.sort(function (a, b) {
        var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
        return (ra.top - rb.top) || (ra.left - rb.left);
      });
      batch.forEach(function (el, i) {
        el.style.setProperty('--d', Math.min(i * 0.06, 0.48) + 's');
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
  }

  // "$2,400.50" -> { pre: "$", num: 2400.5, dec: 2, sep: true, post: "" }
  function parseNum(s) {
    var m = String(s).match(/^([^0-9-]*)(-?[0-9][0-9,]*)(\.[0-9]+)?(.*)$/);
    if (!m) return null;
    return {
      pre: m[1],
      num: parseFloat(m[2].replace(/,/g, '') + (m[3] || '')),
      dec: m[3] ? m[3].length - 1 : 0,
      sep: m[2].indexOf(',') > -1,
      post: m[4]
    };
  }
  function fmtNum(n, f) {
    var s = n.toFixed(f.dec);
    if (f.sep) {
      var p = s.split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      s = p.join('.');
    }
    return f.pre + s + f.post;
  }

  function initCounters(root, state) {
    root.querySelectorAll('[data-ps-count]').forEach(function (el) {
      var parts = el.getAttribute('data-ps-count').split('|');
      var to = parseNum(parts[parts.length - 1]);
      var from = parts.length > 1 ? parseNum(parts[0]) : null;
      if (!to) return;
      var start = from ? from.num : 0;
      var raf = 0;
      function run() {
        cancelAnimationFrame(raf);
        if (still()) { el.textContent = fmtNum(to.num, to); return; }
        var t0 = performance.now();
        var dur = 1800;
        (function tick(now) {
          var p = Math.min(1, (now - t0) / dur);
          var e = 1 - Math.pow(1 - p, 4);
          el.textContent = fmtNum(start + (to.num - start) * e, to);
          if (p < 1) raf = requestAnimationFrame(tick);
        })(t0);
      }
      state.onWake.push(function (first) { if (first) run(); });
      state.onSleep.push(function () { cancelAnimationFrame(raf); el.textContent = fmtNum(to.num, to); });
    });
  }

  function initTimers(root, state) {
    root.querySelectorAll('[data-ps-timer]').forEach(function (el) {
      var p = el.getAttribute('data-ps-timer').split(':').map(function (n) { return parseInt(n, 10) || 0; });
      var total = p.length > 1 ? p[0] * 60 + p[1] : p[0];
      if (!total) return;
      var left = total;
      var id = 0;
      function paint() {
        var m = Math.floor(left / 60), s = left % 60;
        el.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      }
      state.onWake.push(function () {
        if (still() || id) return;
        id = setInterval(function () {
          left = left > 0 ? left - 1 : total;
          paint();
        }, 1000);
      });
      state.onSleep.push(function () { clearInterval(id); id = 0; });
    });
  }

  // [data-ps-presets="A|B|C"] on the timeline tile: the dropped preset takes
  // turns, one name per loop (swapped while the label is hidden).
  function initPresets(root) {
    root.querySelectorAll('[data-ps-presets]').forEach(function (tl) {
      var names = tl.getAttribute('data-ps-presets').split('|').map(function (n) { return n.trim(); }).filter(Boolean);
      var label = tl.querySelector('.psum-tl-drop');
      if (names.length < 2 || !label) return;
      var i = 0;
      label.addEventListener('animationiteration', function (e) {
        if (e.target !== label) return;
        i = (i + 1) % names.length;
        label.textContent = names[i];
      });
    });
  }

  function init(root) {
    if (root.dataset.psInit) return;
    root.dataset.psInit = '1';
    var state = { onSleep: [], onWake: [], inView: false, awake: false, seen: false };

    initKeys(root);
    initEntrance(root);
    initCounters(root, state);
    initTimers(root, state);
    initPresets(root);

    function update() {
      var awake = state.inView && !document.hidden;
      if (awake === state.awake) return;
      state.awake = awake;
      if (awake) {
        root.removeAttribute('data-asleep');
        // counters replay each time the grid comes back into view, not on a tab switch
        var fresh = !state.seen;
        state.seen = true;
        state.onWake.forEach(function (fn) { fn(fresh); });
      } else {
        root.setAttribute('data-asleep', '');
        state.onSleep.forEach(function (fn) { fn(); });
      }
    }
    root.setAttribute('data-asleep', '');
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var v = entries[entries.length - 1].isIntersecting;
        if (!v) state.seen = false;
        state.inView = v;
        update();
      }, { threshold: 0.15 }).observe(root);
    } else {
      state.inView = true;
      update();
    }
    document.addEventListener('visibilitychange', update);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-product-summary]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
